import {
  Box, Button, Grid, Stack,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import CloseButton from '../../Buttons/Close';
import BlockTextListData from './BlockTextListData';
import SubBlockText from './SubBlockText';
import BlockTextListColumnData from './BlockTextListColumnData';
import styles from './BlockTextList.module.css';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';

function BlockTextList({
  data, isEditing, isTemplate, editHandler, showImageModal, onCloseImageModal,
}: BlockProps) {
  const [props, setProps] = useState<BlockTextListData>(data.data as BlockTextListData);

  useEffect(() => {
    setProps(data.data as BlockTextListData);
  }, [data.data]);

  const handleAddColumn = () => {
    props.columns.push(BlockTextListColumnData.getDefault());
    editHandler(data.id, props);
  };

  const columns = useMemo(() => {
    if (props.columns && props.columns.length > 0) {
      return props.columns.map((column, colIndex) => {
        const currentColumn = props.columns[colIndex].data;
        return (
          <Grid
            key={column.id}
            item
            xs={6}
            sx={[Utils.hoverBorder(isEditing), { padding: 2 }]}
            alignItems="center"
            justifyContent="center"
            className={styles.textItem}
          >
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.columns.splice(colIndex, 1);
                editHandler(data.id, props);
              }}
            />

            {column.data.map((item, index) => {
              const currentSubBlock = currentColumn[index];
              return (
                <SubBlockText
                  key={item.id}
                  isEditing={isEditing}
                  isTemplate={isTemplate}
                  data={item}
                  onTitleUpdate={(newTitle) => {
                    currentSubBlock.title.value = newTitle;
                    editHandler(data.id, props);
                  }}
                  onTextUpdate={(newText) => {
                    currentSubBlock.text.value = newText;
                    editHandler(data.id, props);
                  }}
                  onSubBlockDelete={() => {
                    currentColumn.splice(index, 1);
                    editHandler(data.id, props);
                  }}
                  onTextDelete={() => {
                    currentSubBlock.text.value = undefined;
                    editHandler(data.id, props);
                  }}
                  onTitleDelete={() => {
                    currentSubBlock.title.value = undefined;
                    editHandler(data.id, props);
                  }}
                />
              );
            })}

          </Grid>
        );
      });
    }

    return [];
  }, [data.id, editHandler, isEditing, props, isTemplate]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Grid
      id={elementUuid}
      container
      spacing={0}
      alignItems="start"
      justifyContent="start"
      sx={{
        minHeight: '250px',
        overflow: 'hidden',
        padding: 2,
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.url}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, url: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="block-content-wrapper"
      >
        <Box className={styles.listsContainer}>
          {columns}
        </Box>
      </Grid>
      {isEditing && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: '100%' }}>
          <Button color="primary" onClick={handleAddColumn}>
            <AddCircleRoundedIcon />
          </Button>
        </Stack>
      )}
    </Grid>
  );
}

export default BlockTextList;
